import { useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import { api, type CsvElonezetValasz } from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Törzsadat CSV visszatöltése, diff-előnézettel. (Megbízás 7.3)
 *
 * Két lépés: előbb megmutatjuk, **mi változna**, és csak a jóváhagyás után
 * írunk. A párosítás az ID alapján megy, nem a név alapján — így a
 * névjavítás nem csinál duplikátumot. A fájlból hiányzó éttermeket nem
 * törli: részleges fájl is visszatölthető.
 */
export function CsvVisszatoltes({ onKesz }: { onKesz: () => void }) {
  const betolthet = useMuvelet('import');
  const [tartalom, setTartalom] = useState<string | null>(null);
  const [fajlnev, setFajlnev] = useState('');
  const [elonezet, setElonezet] = useState<CsvElonezetValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);
  const [kesz, setKesz] = useState<string | null>(null);

  async function fajlValasztas(fajl: File) {
    setHiba(null);
    setKesz(null);
    setElonezet(null);
    setFajlnev(fajl.name);
    setMunkaban(true);
    try {
      const szoveg = await fajl.text();
      setTartalom(szoveg);
      setElonezet(
        await api.szoveg<CsvElonezetValasz>('/api/export/torzs-csv/elonezet', szoveg),
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A fájlt nem sikerült beolvasni.');
    } finally {
      setMunkaban(false);
    }
  }

  async function vegrehajt() {
    if (!tartalom) return;
    setMunkaban(true);
    setHiba(null);
    try {
      const v = await api.szoveg<{ modositott: number; uj: number; kihagyott: number }>(
        '/api/export/torzs-csv/visszatoltes',
        tartalom,
      );
      setKesz(
        `Kész: ${v.modositott} étterem módosítva, ${v.uj} új felvéve` +
          (v.kihagyott ? `, ${v.kihagyott} hibás sor kihagyva` : '') +
          '.',
      );
      setElonezet(null);
      setTartalom(null);
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A visszatöltés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  const modosulok = (elonezet?.valtozasok ?? []).filter((v) => v.fajta === 'modosul');
  const ujak = (elonezet?.valtozasok ?? []).filter((v) => v.fajta === 'uj');
  const hibasok = (elonezet?.valtozasok ?? []).filter((v) => v.fajta === 'hiba');

  // Akinek nincs rá engedélye, annak a felület sem kínálja fel. (3.0 / 1.)
  if (!betolthet) {
    return <div className="hint">A fájlból való betöltés külön engedélyhez kötött. Szólj az adminisztrátornak.</div>;
  }

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4>CSV visszatöltés</h4>

      <div className="btns" style={{ marginTop: 0 }}>
        <label className="btn sm" style={{ cursor: 'pointer', margin: 0 }}>
          Fájl kiválasztása
          <input
            type="file"
            accept=".csv,.txt"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void fajlValasztas(f);
              e.target.value = '';
            }}
          />
        </label>
        {fajlnev && <span className="hint">{fajlnev}</span>}
        {munkaban && <span className="hint">Dolgozom…</span>}
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}
      {kesz && <div className="uzenet ok">{kesz}</div>}

      {elonezet && (
        <>
          <div className="cards" style={{ marginTop: 12 }}>
            <div className="card">
              <em>Módosul</em>
              <b>{elonezet.modosul}</b>
              <span>étterem</span>
            </div>
            <div className="card">
              <em>Új</em>
              <b>{elonezet.uj}</b>
              <span>felvétel</span>
            </div>
            <div className="card">
              <em>Változatlan</em>
              <b>{elonezet.valtozatlan}</b>
              <span>sor</span>
            </div>
            <div className={`card${elonezet.hibas > 0 ? ' warn2' : ''}`}>
              <em>Hibás</em>
              <b>{elonezet.hibas}</b>
              <span>kimarad</span>
            </div>
            <div className="card">
              <em>Érintetlen</em>
              <b>{elonezet.erintetlen}</b>
              <span>nincs a fájlban</span>
            </div>
          </div>

          <div className="uzenet flag">
            A fájlból hiányzó {elonezet.erintetlen} éttermet a program{' '}
            <b>nem törli</b> — részleges fájl is visszatölthető.
          </div>

          {hibasok.length > 0 && (
            <>
              <p className="lbl" style={{ marginTop: 14 }}>
                Hibás sorok — ezek kimaradnak
              </p>
              <div className="matrix" style={{ maxHeight: 200, overflow: 'auto' }}>
                <table className="tt2">
                  <tbody>
                    {hibasok.map((v) => (
                      <tr key={v.csv_sor}>
                        <td className="calc2" style={{ width: 60 }}>
                          {v.csv_sor}. sor
                        </td>
                        <td style={{ padding: '6px 10px' }}>{v.nev || '—'}</td>
                        <td style={{ padding: '6px 10px', color: 'var(--bad)' }}>{v.hiba}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(modosulok.length > 0 || ujak.length > 0) && (
            <>
              <p className="lbl" style={{ marginTop: 14 }}>
                Mi változna
              </p>
              <div className="matrix" style={{ maxHeight: 340, overflow: 'auto' }}>
                <table className="tt2">
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>Sor</th>
                      <th>Étterem</th>
                      <th>Mező</th>
                      <th>Régi érték</th>
                      <th>Új érték</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ujak.map((v) => (
                      <tr key={`uj-${v.csv_sor}`}>
                        <td className="calc2">{v.csv_sor}.</td>
                        <td className="nm3">
                          <b>{v.nev}</b>
                          <small style={{ color: 'var(--ok)' }}>új felvétel</small>
                        </td>
                        <td colSpan={3} style={{ padding: '6px 10px', color: 'var(--muted)' }}>
                          {v.mezok.map((m) => `${m.cimke}: ${ertekSzoveg(m.uj)}`).join(' · ')}
                        </td>
                      </tr>
                    ))}
                    {modosulok.flatMap((v) =>
                      v.mezok.map((m, i) => (
                        <tr key={`${v.csv_sor}-${m.mezo}`}>
                          <td className="calc2">{i === 0 ? `${v.csv_sor}.` : ''}</td>
                          <td className="nm3">{i === 0 ? <b>{v.nev}</b> : ''}</td>
                          <td style={{ padding: '6px 10px' }}>{m.cimke}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--muted)' }}>
                            {ertekSzoveg(m.regi)}
                          </td>
                          <td style={{ padding: '6px 10px', fontWeight: 600 }}>
                            {ertekSzoveg(m.uj)}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="btns">
            <button
              className="btn pri sm"
              disabled={munkaban || (elonezet.modosul === 0 && elonezet.uj === 0)}
              onClick={() => void vegrehajt()}
            >
              {elonezet.modosul === 0 && elonezet.uj === 0
                ? 'Nincs mit visszatölteni'
                : `Visszatöltés (${elonezet.modosul + elonezet.uj} sor)`}
            </button>
            <button
              className="btn sm"
              onClick={() => {
                setElonezet(null);
                setTartalom(null);
                setFajlnev('');
              }}
            >
              Mégsem
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ertekSzoveg(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'igen' : 'nem';
  if (typeof v === 'number') return ft(v);
  return String(v);
}
